import { useEffect, useState, useCallback, useMemo } from "react";
import { useAppDispatch } from "hooks";
import {
  FormLayout,
  TextField,
  Form,
  Button,
  Banner,
  Checkbox,
  Select,
  Text,
} from "@shopify/polaris";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { fetchProjectBoardWithCurrentFilters } from "containers/project-board/project-board-container.slice";
import { createProject, updateProject } from "./project-form.service";
import { fetchProjectById } from "services/project.service";
import ProjectColumnSelect from "components/project-column-select/project-column-select";
import CustomerSelect from "components/customer-select/CustomerSelect";
import ProductsSelect from "components/products-select/ProductsSelect";
import Loading from "components/loading/Loading";
import { fetchProjectDetails } from "containers/project-view-container/project-container.slice";
import DatePickerx from "features/datepicker/date-picker";
import UserSelectByPermissions from "features/user-select-by-permission/user-select-by-permission";
import {
  celebrateWonDeal,
  formatWonToast,
} from "features/project-board/project-motion";
import { toast } from "sonner";

function ProjectForm({ projectBoardId, projectBoardColumnId, id }) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams();
  const [name, setName] = useState();
  const [description, setDescription] = useState();
  const [projectBoardColumn, setProjectBoardColumn] =
    useState(projectBoardColumnId);
  const [user, setUser] = useState();
  const [customer, setCustomer] = useState();
  const [order, setOrder] = useState();
  const [products, setProducts] = useState();
  const [errMsg, setErrMsg] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isImportant, setIsImportant] = useState(false);
  const [dueDate, setDueDate] = useState("");
  const [isResolved, setIsResolved] = useState(false);
  const [outcome, setOutcome] = useState("");
  const [initialOutcome, setInitialOutcome] = useState("");
  const [valueEur, setValueEur] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  const outcomeOptions = useMemo(
    () => [
      {
        label: t("project_outcome_open", { defaultValue: "Open" }),
        value: "",
      },
      {
        label: t("project_outcome_won", { defaultValue: "Won" }),
        value: "WON",
      },
      {
        label: t("project_outcome_lost", { defaultValue: "Lost" }),
        value: "LOST",
      },
      {
        label: t("project_outcome_deleted", { defaultValue: "Deleted" }),
        value: "DELETED",
      },
    ],
    [t]
  );

  // Handle checkbox state changes generically
  const handleCheckboxChange = useCallback(
    (setter) => (value) => {
      setter(value);
    },
    []
  );

  const handleFormSubmit = async () => {
    setErrMsg();
    if (!name || !name.trim()) {
      setErrMsg("project-name");
      return;
    }
    if (!projectBoardColumn) {
      setErrMsg("project-board-column-create");
      return;
    }
    const formData = {
      id,
      projectBoardColumn,
      name,
      description,
      user,
      customer,
      order: order ? order : undefined,
      products,
      isImportant,
      isResolved,
      dueDate,
      outcome: outcome || null,
      valueEur: valueEur === "" ? null : valueEur,
      contactName: contactName || null,
      contactEmail: contactEmail || null,
      contactPhone: contactPhone || null,
    };
    const action = id ? updateProject : createProject;
    try {
      setIsLoading(true);
      await action(formData, companyId);
      if (outcome === "WON" && initialOutcome !== "WON") {
        await celebrateWonDeal();
        toast.success(
          formatWonToast(valueEur === "" ? null : Number(valueEur))
        );
      }
      dispatch(fetchProjectBoardWithCurrentFilters(companyId, projectBoardId));
      if (id) dispatch(fetchProjectDetails({ companyId, projectId: id }));
      setIsLoading(false);
      // The window closes on success: a banner at the top of a long form is out of sight on a phone.
      dispatch(setIsModalxOpen(false));
      dispatch(setDestroy());
    } catch (e) {
      console.error(e);
      setErrMsg("error");
      setIsLoading(false);
    }
  };

  const fetchData = useCallback(
    async (id) => {
      setIsLoading(true);
      const data = await fetchProjectById(companyId, id);
      setName(data.name);
      setDescription(data.description);
      setProjectBoardColumn(data.projectBoardColumn.id);
      setUser(data.user?.id);
      setCustomer(data.customer?.id);
      setOrder(data.order);
      setProducts(data.products);
      setIsImportant(!!data.isImportant);
      setIsResolved(!!data.isResolved);
      setDueDate(data.dueDate);
      setOutcome(data.outcome || "");
      setInitialOutcome(data.outcome || "");
      setValueEur(
        data.valueEur === null || data.valueEur === undefined
          ? ""
          : String(data.valueEur)
      );
      setContactName(data.contactName || "");
      setContactEmail(data.contactEmail || "");
      setContactPhone(data.contactPhone || "");
      setIsLoading(false);
    },
    [companyId]
  );

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id, fetchData]);

  if (isLoading && !name) {
    return <Loading />;
  }

  const handleDueDateChange = (value) => setDueDate(value);

  return (
    <Form className="project-board-column-form">
      <FormLayout>
        <div className="form-warnings">
          {errMsg && (
            <div className="">
              <Banner status="critical">{t(errMsg)}</Banner>
            </div>
          )}
        </div>
        <ProjectColumnSelect
          value={projectBoardColumn}
          onChange={setProjectBoardColumn}
          projectBoardId={projectBoardId}
          isRequired={true}
        />
        <CustomerSelect value={customer} onChange={setCustomer} />
        <UserSelectByPermissions
          value={user}
          onChange={setUser}
          label={"select_user"}
          Permission={"Access project"}
        />

        <TextField
          label={t("project-name")}
          id="name"
          name="name"
          value={name}
          onChange={setName}
          requiredIndicator={true}
        />
        <TextField
          label={t("project-description")}
          id="description"
          name="description"
          multiline={4}
          value={description}
          onChange={setDescription}
        />
        <TextField
          label={t("project_value_eur", { defaultValue: "Value (EUR)" })}
          id="valueEur"
          name="valueEur"
          type="number"
          value={valueEur}
          onChange={setValueEur}
          autoComplete="off"
          prefix="€"
        />
        <Select
          label={t("project_outcome", { defaultValue: "Outcome" })}
          options={outcomeOptions}
          value={outcome}
          onChange={setOutcome}
        />

        <Text as="h3" variant="headingSm">
          {t("project_contact", { defaultValue: "Client contact" })}
        </Text>
        <TextField
          label={t("contact-name", { defaultValue: "Name" })}
          id="contactName"
          name="contactName"
          value={contactName}
          onChange={setContactName}
          autoComplete="off"
        />
        <TextField
          label={t("email", { defaultValue: "Email" })}
          id="contactEmail"
          name="contactEmail"
          type="email"
          value={contactEmail}
          onChange={setContactEmail}
          autoComplete="off"
        />
        <TextField
          label={t("phone", { defaultValue: "Phone" })}
          id="contactPhone"
          name="contactPhone"
          value={contactPhone}
          onChange={setContactPhone}
          autoComplete="off"
        />

        <TextField
          label={t("project-order")}
          id="order"
          name="order"
          type="number"
          value={order}
          onChange={setOrder}
        />
        <ProductsSelect initialProducts={products} onChange={setProducts} />
        <DatePickerx
          name="dueDate"
          title={t("due_date")}
          onChange={handleDueDateChange}
          selected={dueDate}
        />
        <Checkbox
          label={t("important")}
          checked={isImportant}
          onChange={handleCheckboxChange(setIsImportant)}
        />
        <Checkbox
          label={t("is_resolved")}
          checked={isResolved}
          onChange={handleCheckboxChange(setIsResolved)}
        />
        <Button
          primary
          onClick={handleFormSubmit}
          disabled={!name || !projectBoardId}
          loading={isLoading}
        >
          {t("save")}
        </Button>
      </FormLayout>
    </Form>
  );
}

export default ProjectForm;
